import PropTypes from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { ActionButton } from '@augmentt-dev/ui-components'
import PopoverMenu, { ACTION_IDS } from '../../../../../components/PopoverMenu'
import useIsAuthorized from '../../../ThreatAlerts/useIsAuthorized'
import { GWS, OFFICE365 } from '../../../engage/utils'
import EmployeeActionsSection from '../EmployeeActionsSection'
import styles from './EmployeeActionToolbar.module.scss'
import { getPopoverMenuConfig } from './popoverMenuConfig'

const POPOVER_TOGGLE_PROPS = {
  icon: 'fas fa-ellipsis',
  variant: 'light',
  className: styles.popoverToggle,
}

function EmployeeActionToolbar({
  employee,
  selectedCustomer,
  onEdit,
  canPerformActions = true,
  overrideEngageLicensing = false,
  setAction,
  setModal,
  openIntegrationModal,
  selectedIntegration,
}) {
  const integrationType = selectedIntegration?.IntegrationApplication?.application_name === GWS ? GWS : OFFICE365
  const isGws = integrationType === GWS
  const isIntegrationAuthorized = useIsAuthorized({
    integrationAppName: selectedIntegration?.IntegrationApplication?.application_name,
    selectedCustomerId: selectedCustomer?.id,
  })
  const isAuthorized = canPerformActions && isIntegrationAuthorized
  const selectedEmployees = useMemo(() => [employee], [employee])

  const handleEdit = useCallback(() => {
    onEdit()
  }, [onEdit])

  const popoverConfig = useMemo(() => getPopoverMenuConfig(integrationType), [integrationType])
  const popoverContext = useMemo(() => {
    const context = {
      isAuthorized,
      selectedIntegration,
      integrationType,
      setAction,
      setModal,
    }

    if (!isGws) {
      context.openIntegrationModal = openIntegrationModal
      context.actionConditions = {
        [ACTION_IDS.GlobalAddressList]: (emp) => ({
          isAvailable: emp?.isUserMailBoxAvailable || emp?.isUserSharedMailbox,
        }),
        [ACTION_IDS.ManageEmailForwarding]: (emp) => ({
          isAvailable: emp?.isUserMailBoxAvailable || emp?.isUserSharedMailbox,
        }),
      }
    }

    return context
  }, [isAuthorized, selectedIntegration, integrationType, setAction, setModal, isGws, openIntegrationModal])

  return (
    <div className={styles.toolbar}>
      <div className={`d-flex align-items-center ${styles.actions}`}>
        <ActionButton
          icon="fas fa-pen-to-square"
          label={<FormattedMessage id="GENERAL.EDIT" />}
          variant="primary"
          disabled={!canPerformActions}
          onClick={handleEdit}
        />
        {employee?.employeeIdentifier && (
          <EmployeeActionsSection
            isAuthorized={isAuthorized}
            openIntegrationModal={openIntegrationModal}
            selectedEmployees={selectedEmployees}
            overrideEngageLicensing={overrideEngageLicensing}
            integrationType={integrationType}
            setAction={setAction}
            setModal={setModal}
          />
        )}
        {employee?.employeeIdentifier && canPerformActions && (
          <PopoverMenu
            config={popoverConfig}
            employee={employee}
            context={popoverContext}
            toggleProps={POPOVER_TOGGLE_PROPS}
          />
        )}
      </div>
    </div>
  )
}

EmployeeActionToolbar.propTypes = {
  employee: PropTypes.shape({
    employeeIdentifier: PropTypes.string,
  }),
  selectedCustomer: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onEdit: PropTypes.func.isRequired,
  canPerformActions: PropTypes.bool,
  overrideEngageLicensing: PropTypes.bool,
  setAction: PropTypes.func,
  setModal: PropTypes.func,
  openIntegrationModal: PropTypes.func,
  selectedIntegration: PropTypes.shape({
    IntegrationApplication: PropTypes.shape({
      application_name: PropTypes.string,
    }),
  }),
}

export default EmployeeActionToolbar
